import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useBookingPage, PaymentInfo } from '../../BookingPageContext';
import paymentService from '../../../../services/paymentService';
import LoadingSpinner from '../../../../components/core/LoadingSpinner/LoadingSpinner';
import { formatCurrency } from '../../../../utils/currency';

/**
 * Phase 3 — Pay Now payment screen.
 *
 * Renders after the booking form is submitted IF the meeting type collects
 * payment up front. The booking row already exists server-side in
 * `pending` state with a 10-minute slot hold; this screen runs Stripe
 * Elements, calls `stripe.confirmPayment()`, then POSTs the result to
 * our `/confirm-payment` endpoint which atomically flips the booking to
 * confirmed and fires the deferred calendar/email side-effects.
 *
 * Direct Charges architecture — the PaymentIntent was created on the host's
 * connected account, so the `stripeAccount` option is required when loading
 * stripe.js OR the PaymentElement will fail with "no such payment intent."
 */
const PaymentScreen: React.FC = () => {
  const { paymentInfo, meetingType } = useBookingPage();

  if (!paymentInfo) return null;

  // Phase 3 — graceful failure when STRIPE_PUBLISHABLE_KEY isn't set on the
  // server. Without it, loadStripe() returns null and the Pay button stays
  // disabled with no explanation. Surface a clear error instead.
  if (!paymentInfo.publishableKey) {
    return <PaymentUnavailableNotice />;
  }

  return <PaymentScreenInner paymentInfo={paymentInfo} meetingTitle={meetingType?.title} />;
};

const PaymentUnavailableNotice: React.FC = () => {
  const { t } = useTranslation('payments');
  return (
    <div className="max-w-xl mx-auto p-6 bg-red-50 border border-red-200 rounded-lg">
      <h2 className="text-lg font-semibold text-red-900 mb-2">
        {t('payNow.unavailableTitle', 'Payment temporarily unavailable')}
      </h2>
      <p className="text-sm text-red-800">
        {t(
          'payNow.unavailableBody',
          "We couldn't load the payment form. Please contact the host to complete your booking."
        )}
      </p>
    </div>
  );
};

const PaymentScreenInner: React.FC<{
  paymentInfo: PaymentInfo;
  meetingTitle?: string;
}> = ({ paymentInfo, meetingTitle }) => {
  // Memoize the Stripe promise so we don't re-load the JS bundle on every
  // render. Capturing connectedAccountId in the closure is essential for
  // Direct Charges to route correctly.
  const stripePromise = useMemo<Promise<Stripe | null>>(
    () => loadStripe(paymentInfo.publishableKey, { stripeAccount: paymentInfo.connectedAccountId }),
    [paymentInfo.publishableKey, paymentInfo.connectedAccountId]
  );

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: paymentInfo.clientSecret,
        appearance: { theme: 'stripe' as const },
      }}
    >
      <PaymentForm paymentInfo={paymentInfo} meetingTitle={meetingTitle} />
    </Elements>
  );
};

const PaymentForm: React.FC<{
  paymentInfo: PaymentInfo;
  meetingTitle?: string;
}> = ({ paymentInfo, meetingTitle }) => {
  const { t } = useTranslation('payments');
  const { setBookingSuccess, setPaymentInfo } = useBookingPage();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Stripe's PaymentElement takes a moment to fetch payment methods and render.
  // Track its ready state so we can show a spinner instead of a blank gap.
  const [ready, setReady] = useState(false);
  const [remainingMs, setRemainingMs] = useState<number>(() =>
    Math.max(0, new Date(paymentInfo.expiresAt).getTime() - Date.now())
  );

  // Live countdown
  useEffect(() => {
    const id = setInterval(() => {
      const remaining = Math.max(0, new Date(paymentInfo.expiresAt).getTime() - Date.now());
      setRemainingMs(remaining);
      if (remaining <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [paymentInfo.expiresAt]);

  const expired = remainingMs <= 0;
  const mm = Math.floor(remainingMs / 60_000);
  const ss = Math.floor((remainingMs % 60_000) / 1000)
    .toString()
    .padStart(2, '0');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Defense-in-depth: the button is already disabled until `ready`, but guard
    // here too so a stray submit (e.g. Enter key) can never fire confirmPayment
    // before the PaymentElement is interactive.
    if (!stripe || !elements || !ready || submitting || expired) return;

    setSubmitting(true);
    setError(null);

    // The whole flow is wrapped so `submitting` is ALWAYS reset in `finally`.
    // Previously confirmPayment was awaited with no try/catch — any rejection
    // (transient Stripe integration error, or a click before the Element was
    // fully ready) left `submitting` stuck at true forever, which both showed
    // "Processing…" indefinitely AND blocked every later submit via the guard
    // above. The finally guarantees the button can never get permanently stuck.
    try {
      // Run Stripe's client-side confirmation — handles 3DS / SCA if needed.
      // redirect: 'if_required' keeps the invitee on this page unless 3DS
      // forces a redirect (rare in test mode, common with some EU cards).
      // The clientSecret already lives on the <Elements> provider, so we use
      // the Element-data overload (no clientSecret here) — Stripe's documented
      // path for collecting payment details via the PaymentElement.
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });

      if (stripeError) {
        setError(stripeError.message || t('payNow.errors.generic', 'Payment failed. Please try again.'));
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Server-authoritative confirm. Server re-checks with Stripe and only
        // then fires calendar/email side-effects.
        const res = await paymentService.confirmBookingPayment(paymentInfo.bookingId);
        if (res.status === 'paid') {
          setBookingSuccess(true);
          setPaymentInfo(null);
          return;
        }
        if (res.status === 'pending') {
          // Webhook hasn't caught up yet; show a soft message and retry once.
          setError(t('payNow.errors.stillPending', 'Payment is still processing. Please try again in a moment.'));
        } else {
          setError(res.message || t('payNow.errors.generic', 'Payment failed. Please try again.'));
        }
        return;
      }

      // Unexpected state (e.g. requires_action with no redirect). Surface a
      // retryable error rather than silently doing nothing.
      setError(t('payNow.errors.generic', 'Payment failed. Please try again.'));
    } catch (err: any) {
      setError(err?.message || t('payNow.errors.generic', 'Payment failed. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-6 bg-white rounded-lg shadow-md border border-gray-200">
      <h2 className="text-xl font-semibold text-gray-900 mb-1">
        {t('payNow.title', 'Complete your payment')}
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        {meetingTitle
          ? t('payNow.subtitle', 'Confirm payment to finalise your booking for {{meeting}}.', {
              meeting: meetingTitle,
            })
          : t('payNow.subtitleNoMeeting', 'Confirm payment to finalise your booking.')}
      </p>

      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mb-4 flex items-center justify-between">
        <span className="text-sm text-gray-600">{t('payNow.amountLabel', 'Amount due')}</span>
        <span className="text-lg font-semibold text-gray-900">
          {formatCurrency(paymentInfo.amount, paymentInfo.currency)}
        </span>
      </div>

      <div
        className={`mb-4 px-3 py-2 rounded-md text-sm flex items-center justify-between ${
          expired
            ? 'bg-red-50 border border-red-200 text-red-700'
            : 'bg-yellow-50 border border-yellow-200 text-yellow-800'
        }`}
      >
        <span>
          {expired
            ? t('payNow.expired', 'Your reservation expired. Please book again.')
            : t('payNow.holdingSlot', 'Your time slot is held for the next')}
        </span>
        {!expired && (
          <span className="font-mono font-semibold">
            {mm}:{ss}
          </span>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        {/* The PaymentElement is ALWAYS mounted and visible (never display:none).
            Mounting it inside a hidden container is a known Stripe reliability
            anti-pattern — it can fire onReady before the card fields paint,
            opening a window where the Pay button is enabled but the form looks
            empty (the "clickable before the form appears" bug). Instead we keep
            it visible and overlay the loading spinner until onReady fires; the
            Pay button stays disabled (via `ready`) the whole time. */}
        <div className="relative min-h-[3rem]">
          {!ready && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/90 py-12 text-gray-500">
              <LoadingSpinner size="lg" />
              <span className="text-sm">
                {t('payNow.loadingForm', 'Loading secure payment form…')}
              </span>
            </div>
          )}
          <PaymentElement
            onReady={() => setReady(true)}
            onLoadError={() => {
              // Stop the spinner and surface a message rather than hang forever.
              setReady(true);
              setError(t('payNow.errors.generic', 'Payment failed. Please try again.'));
            }}
          />
        </div>
        {error && (
          <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={!stripe || !elements || !ready || submitting || expired}
          className="btn-primary w-full mt-4"
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <LoadingSpinner /> {t('payNow.processing', 'Processing…')}
            </span>
          ) : (
            t('payNow.payButton', 'Pay {{amount}}', {
              amount: formatCurrency(paymentInfo.amount, paymentInfo.currency),
            })
          )}
        </button>
      </form>

      <p className="mt-4 text-xs text-gray-500 text-center">
        {t('payNow.processedBy', 'Payments processed securely.')}
      </p>
    </div>
  );
};

export default PaymentScreen;
